import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';

const libraries = ['places', 'marker'];

const mapContainerStyle = {
  width: '100%',
  height: '400px'
};

const defaultCenter = {
  lat: 40.7128,
  lng: -74.0060 // New York City
};

const LocationPicker = ({ onLocationSelect, initialLocation = null, businessAddress = null }) => {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(initialLocation || (businessAddress?.coordinates ? {
    lat: businessAddress.coordinates.latitude,
    lng: businessAddress.coordinates.longitude
  } : null));
  const [mapMarker, setMapMarker] = useState(null);
  const [address, setAddress] = useState({
    street: businessAddress?.street || '',
    city: businessAddress?.city || '',
    state: businessAddress?.state || '',
    zipcode: businessAddress?.zipcode || ''
  });
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  // Initialize autocomplete when maps is loaded
  useEffect(() => {
    if (isLoaded && searchInputRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        {
          types: ['establishment', 'geocode'],
          componentRestrictions: { country: 'us' }
        }
      );

      autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
    }

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded]);

  // Initialize search value with existing address (only once)
  useEffect(() => {
    if (!initialized && businessAddress && (businessAddress.street || businessAddress.city)) {
      const addressParts = [];
      if (businessAddress.street) addressParts.push(businessAddress.street);
      if (businessAddress.city) addressParts.push(businessAddress.city);
      if (businessAddress.state) addressParts.push(businessAddress.state);
      if (businessAddress.zipcode) addressParts.push(businessAddress.zipcode);
      
      setSearchValue(addressParts.join(', '));
      setInitialized(true);
    }
  }, [businessAddress, initialized]);

  const handlePlaceSelect = useCallback(() => {
    const place = autocompleteRef.current.getPlace();
    if (place.geometry) {
      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      
      setMarker(location);
      map?.panTo(location);
      
      // Extract address components
      const addressComponents = extractAddressComponents(place);
      setAddress(addressComponents);
      
      // Notify parent component
      onLocationSelect({
        coordinates: {
          latitude: location.lat,
          longitude: location.lng
        },
        ...addressComponents
      });
    }
  }, [map, onLocationSelect]);

  const extractAddressComponents = (place) => {
    const components = {
      street: '',
      city: '',
      state: '',
      zipcode: ''
    };

    if (place.address_components) {
      place.address_components.forEach(component => {
        const types = component.types;
        
        if (types.includes('street_number')) {
          components.street = component.long_name + ' ';
        }
        if (types.includes('route')) {
          components.street += component.long_name;
        }
        if (types.includes('locality') || types.includes('sublocality')) {
          components.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          components.state = component.long_name;
        }
        if (types.includes('postal_code')) {
          components.zipcode = component.long_name;
        }
      });
    }

    return components;
  };

  const handleMapClick = useCallback((event) => {
    const location = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    
    setMarker(location);
    setIsSearching(true);
    
    // Reverse geocode to get address
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { location: location },
      (results, status) => {
        setIsSearching(false);
        if (status === 'OK' && results[0]) {
          const addressComponents = extractAddressComponents(results[0]);
          setAddress(addressComponents);
          setSearchValue(results[0].formatted_address);
          
          // Notify parent component
          onLocationSelect({
            coordinates: {
              latitude: location.lat,
              longitude: location.lng
            },
            ...addressComponents
          });
        }
      }
    );
  }, [onLocationSelect]);

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          setMarker(location);
          map?.panTo(location);
          
          // Reverse geocode
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: location },
            (results, status) => {
              if (status === 'OK' && results[0]) {
                const addressComponents = extractAddressComponents(results[0]);
                setAddress(addressComponents);
                setSearchValue(results[0].formatted_address);
                
                onLocationSelect({
                  coordinates: {
                    latitude: location.lat,
                    longitude: location.lng
                  },
                  ...addressComponents
                });
              }
            }
          );
        },
        (error) => {
          console.error('Error getting current location:', error);
          alert('Unable to get your current location. Please search for an address or click on the map.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const onLoad = useCallback((map) => {
    setMap(map);
    
    // If we have an initial marker, center on it
    if (marker) {
      map.panTo(marker);
      map.setZoom(15);
    }
  }, [marker]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Create and manage advanced marker
  useEffect(() => {
    if (map && marker) {
      // Remove existing marker
      if (mapMarker) {
        if (mapMarker.map) {
          mapMarker.map = null;
        } else if (mapMarker.setMap) {
          mapMarker.setMap(null);
        }
      }

      let newMarker;
      
      // Try to use AdvancedMarkerElement if available
      if (window.google?.maps?.marker?.AdvancedMarkerElement) {
        newMarker = new window.google.maps.marker.AdvancedMarkerElement({
          map: map,
          position: marker,
          title: 'Selected Location'
        });
      } else {
        // Fallback to traditional Marker
        newMarker = new window.google.maps.Marker({
          map: map,
          position: marker,
          title: 'Selected Location',
          animation: window.google.maps.Animation.DROP
        });
      }

      setMapMarker(newMarker);
    } else if (mapMarker && !marker) {
      // Remove marker if no position
      if (mapMarker.map) {
        mapMarker.map = null;
      } else if (mapMarker.setMap) {
        mapMarker.setMap(null);
      }
      setMapMarker(null);
    }
  }, [map, marker?.lat, marker?.lng]);

  // Cleanup marker on unmount
  useEffect(() => {
    return () => {
      if (mapMarker) {
        if (mapMarker.map) {
          mapMarker.map = null;
        } else if (mapMarker.setMap) {
          mapMarker.setMap(null);
        }
      }
    };
  }, [mapMarker]);

  if (loadError) {
    return <div className="text-red-600">Error loading maps</div>;
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="space-y-3">
        <div className="flex space-x-2">
          <div className="flex-1">
            <input
              ref={searchInputRef}
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search for an address or business..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <button
            type="button"
            onClick={handleCurrentLocation}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            title="Use Current Location"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
        
        <p className="text-sm text-gray-600">
          {marker 
            ? 'Click on the map to select a different location, or search for an address above.'
            : 'Search for an address above, click on the map, or use your current location.'}
        </p>
      </div>

      {/* Map */}
      <div className="relative">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={marker || defaultCenter}
          zoom={marker ? 15 : 11}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false
          }}
        >
          {/* Marker is now handled by useEffect with AdvancedMarkerElement */}
        </GoogleMap>
        
        {isSearching && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="bg-white px-4 py-2 rounded-lg flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
              <span className="text-sm">Getting address...</span>
            </div>
          </div>
        )}
      </div>

      {/* Selected Address Display */}
      {marker && (
        <div className="bg-slate-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Selected Location</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Address:</span>
              <p className="text-gray-600">{address.street || 'N/A'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">City:</span>
              <p className="text-gray-600">{address.city || 'N/A'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">State:</span>
              <p className="text-gray-600">{address.state || 'N/A'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">ZIP Code:</span>
              <p className="text-gray-600">{address.zipcode || 'N/A'}</p>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Coordinates: {marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker; 